import { useCallback, useEffect, useRef, useState } from 'react';

import type { AnalyticsDashboardDateRangePreset } from '../../graphql/__generated__/graphql';
import { fetchAnalyticsDashboard, type AnalyticsFetcher } from './analyticsApi';
import type { AnalyticsDashboardData } from './types';

export type AnalyticsDashboardState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'success'; data: AnalyticsDashboardData };

export type UseAnalyticsDashboardResult = {
  state: AnalyticsDashboardState;
  reload: () => void;
};

// Manages the async lifecycle of the analytics payload. The fetcher is injected
// (defaults to the real one) so tests can drive loading/success/error without
// mocking modules.
export const useAnalyticsDashboard = (
  dateRange: AnalyticsDashboardDateRangePreset,
  fetcher: AnalyticsFetcher = fetchAnalyticsDashboard,
): UseAnalyticsDashboardResult => {
  const [state, setState] = useState<AnalyticsDashboardState>({ status: 'loading' });
  const [attempt, setAttempt] = useState(0);

  // Held in a ref so an inline/unstable fetcher can't retrigger the effect on
  // every render (which would loop load→setState→render→load). The effect only
  // re-runs on mount and explicit reload().
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const reload = useCallback(() => setAttempt(current => current + 1), []);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    setState({ status: 'loading' });

    fetcherRef.current(dateRange, { signal: controller.signal })
      .then(data => {
        if (active && !controller.signal.aborted) setState({ status: 'success', data });
      })
      .catch((error: unknown) => {
        if (active && !controller.signal.aborted) {
          setState({
            status: 'error',
            message: error instanceof Error ? error.message : 'Unable to load analytics.',
          });
        }
      });

    return () => {
      active = false;
      controller.abort();
    };
  }, [attempt, dateRange]);

  return { state, reload };
};
